import React, { useState, useEffect } from 'react';
import { DollarSign, Users, TrendingUp, CheckCircle, Clock, CreditCard, Download, AlertCircle, Calendar, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Payroll = () => {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [payrollData, setPayrollData] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [paymentModal, setPaymentModal] = useState({ show: false, staff: null });
  const [paymentDetails, setPaymentDetails] = useState({ method: 'Bank Transfer', reference: '', remarks: '', bank_id: '' });
  const [banks, setBanks] = useState([]);
  const [workingDaysPerMonth, setWorkingDaysPerMonth] = useState(26); // Default, will fetch from settings

  // Fetch timezone settings to get working days per month
  const fetchTimezoneSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/master-settings/timezone-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data?.working_days_per_month) {
        setWorkingDaysPerMonth(response.data.working_days_per_month);
        console.log('📅 Working days per month from settings:', response.data.working_days_per_month);
      }
    } catch (err) {
      console.log('Using default working days (26)');
    }
  };

  // Fetch bank accounts
  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/accounts/banks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBanks(response.data || []);
    } catch (err) {
      console.error('Error fetching banks:', err);
    }
  };

  // Generate month options
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = -12; i <= 1; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options.reverse();
  };

  const monthOptions = getMonthOptions();

  useEffect(() => {
    fetchStaffList();
    fetchBanks();
    fetchTimezoneSettings(); // Fetch working days setting
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchPayrollData();
    }
  }, [selectedMonth]);

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStaffList(response.data || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  };

  const fetchPayrollData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Check if payroll exists for this month
      const response = await axios.get(`${API_URL}/api/salary/payroll/${selectedMonth}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPayrollData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setPayrollData(null);
      } else {
        console.error('Error fetching payroll:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const generatePayroll = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get attendance summary
      const attendanceResponse = await axios.get(
        `${API_URL}/api/staff-attendance/monthly-summary?month=${selectedMonth}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const attendanceSummary = attendanceResponse.data?.staff_summary || [];

      // Generate payroll for each staff
      const payrollRecords = [];
      
      for (const staff of staffList) {
        try {
          // Get salary template
          const salaryResponse = await axios.get(
            `${API_URL}/api/salary/templates/${staff.id}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          const salaryTemplate = salaryResponse.data;
          const attendance = attendanceSummary.find(a => a.staff_id === staff.id) || {};
          
          // Calculate - Use working days from settings (fetched from timezone settings)
          const STANDARD_WORKING_DAYS = workingDaysPerMonth;
          const presentDays = (attendance.present || 0) + (attendance.late || 0) + ((attendance.half_day || 0) * 0.5) + (attendance.leave || 0);
          
          // Per day salary = Gross / working days per month
          const grossSalary = salaryTemplate.gross_salary || staff.salary || 0;
          const perDaySalary = grossSalary / STANDARD_WORKING_DAYS;
          
          // Actual gross = Per day salary × Present days
          const actualGross = Math.round(perDaySalary * presentDays);
          
          // Deductions also pro-rated
          const perDayDeduction = (salaryTemplate.total_deductions || 0) / STANDARD_WORKING_DAYS;
          const deductions = Math.round(perDayDeduction * presentDays);
          
          // For display purposes
          const workingDays = STANDARD_WORKING_DAYS;
          const attendanceRatio = presentDays / STANDARD_WORKING_DAYS;
          const netSalary = actualGross - deductions;
          
          payrollRecords.push({
            staff_id: staff.id,
            employee_id: staff.employee_id,
            full_name: staff.full_name,
            department: staff.department,
            designation: staff.designation,
            basic_salary: salaryTemplate.basic_salary || 0,
            gross_salary: grossSalary,
            allowances: salaryTemplate.allowances || [],
            deductions_list: salaryTemplate.deductions || [],
            total_allowances: salaryTemplate.total_allowances || 0,
            total_deductions: salaryTemplate.total_deductions || 0,
            working_days: workingDays,
            present_days: presentDays,
            absent_days: attendance.absent || 0,
            leave_days: attendance.leave || 0,
            attendance_percentage: Math.round(attendanceRatio * 100),
            actual_gross: actualGross,
            actual_deductions: deductions,
            net_payable: netSalary,
            status: 'Pending',
            paid_amount: 0,
            paid_on: null,
            payment_method: null,
            payment_reference: null
          });
        } catch (err) {
          // No salary template
          payrollRecords.push({
            staff_id: staff.id,
            employee_id: staff.employee_id,
            full_name: staff.full_name,
            department: staff.department,
            designation: staff.designation,
            basic_salary: 0,
            gross_salary: staff.salary || 0,
            total_allowances: 0,
            total_deductions: 0,
            working_days: 0,
            present_days: 0,
            absent_days: 0,
            leave_days: 0,
            attendance_percentage: 0,
            actual_gross: 0,
            actual_deductions: 0,
            net_payable: 0,
            status: 'No Template',
            paid_amount: 0
          });
        }
      }

      // Save payroll to database
      const saveResponse = await axios.post(
        `${API_URL}/api/salary/payroll/generate`,
        {
          month: selectedMonth,
          records: payrollRecords
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setPayrollData(saveResponse.data);
      alert('Payroll generated successfully!');
    } catch (err) {
      console.error('Error generating payroll:', err);
      alert('Failed to generate payroll. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const openPaymentModal = (staff) => {
    setPaymentDetails({ method: 'Bank Transfer', reference: '', remarks: '', bank_id: banks[0]?.id || '' });
    setPaymentModal({ show: true, staff });
  };

  const processPayment = async () => {
    if (!paymentModal.staff) return;
    
    // Validate bank selection for non-cash payments
    if (paymentDetails.method !== 'Cash' && !paymentDetails.bank_id) {
      alert('Please select a Bank Account');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/salary/payroll/pay`,
        {
          month: selectedMonth,
          staff_id: paymentModal.staff.staff_id,
          amount: paymentModal.staff.net_payable,
          method: paymentDetails.method,
          reference: paymentDetails.reference,
          remarks: paymentDetails.remarks,
          bank_id: paymentDetails.method !== 'Cash' ? paymentDetails.bank_id : null
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert('Payment processed successfully!');
      setPaymentModal({ show: false, staff: null });
      fetchPayrollData();
    } catch (err) {
      console.error('Error processing payment:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to process payment. Please try again.';
      alert(errorMessage);
    }
  };

  const payAll = async () => {
    if (!payrollData?.records?.length) return;
    
    const pendingRecords = payrollData.records.filter(r => r.status === 'Pending' && r.net_payable > 0);
    if (!pendingRecords.length) {
      alert('No pending payments to process.');
      return;
    }

    if (!window.confirm(`Pay all ${pendingRecords.length} pending salaries?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/salary/payroll/pay-all`,
        {
          month: selectedMonth,
          method: 'Bank Transfer'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert('All payments processed successfully!');
      fetchPayrollData();
    } catch (err) {
      console.error('Error processing payments:', err);
      alert('Failed to process payments. Please try again.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Paid':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Paid</span>;
      case 'Pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</span>;
      case 'No Template':
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">No Template</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  // Calculate totals
  const totals = payrollData?.records?.reduce((acc, r) => ({
    totalPayroll: acc.totalPayroll + (r.net_payable || 0),
    totalPaid: acc.totalPaid + (r.status === 'Paid' ? r.net_payable : 0),
    totalPending: acc.totalPending + (r.status === 'Pending' ? r.net_payable : 0),
    paidCount: acc.paidCount + (r.status === 'Paid' ? 1 : 0),
    pendingCount: acc.pendingCount + (r.status === 'Pending' ? 1 : 0)
  }), { totalPayroll: 0, totalPaid: 0, totalPending: 0, paidCount: 0, pendingCount: 0 }) || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Management</h1>
          <p className="text-sm text-gray-500 mt-1">Generate and manage monthly staff payroll</p>
        </div>
        <div className="flex gap-2">
          {payrollData && payrollData.records?.some(r => r.status === 'Pending') && (
            <button 
              onClick={payAll}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <CreditCard className="h-4 w-4" />
              Pay All Pending
            </button>
          )}
          <button className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200">
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Payroll</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {formatCurrency(totals.totalPayroll)}
              </p>
            </div>
            <DollarSign className="h-10 w-10 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Staff Count</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {payrollData?.records?.length || 0}
              </p>
            </div>
            <Users className="h-10 w-10 text-purple-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Paid</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {totals.paidCount || 0}
              </p>
              <p className="text-xs text-gray-400">{formatCurrency(totals.totalPaid)}</p>
            </div>
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {totals.pendingCount || 0}
              </p>
              <p className="text-xs text-gray-400">{formatCurrency(totals.totalPending)}</p>
            </div>
            <Clock className="h-10 w-10 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Month Selection & Generate */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Select Month
            </label>
            <select 
              className="w-full px-3 py-2 border rounded-lg bg-white"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {!payrollData && (
            <button 
              onClick={generatePayroll}
              disabled={generating || loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {generating ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Generating...
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4" />
                  Generate Payroll
                </>
              )}
            </button>
          )}

          {payrollData && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Payroll generated on {formatDate(payrollData.generated_at)}
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Loading payroll data...</p>
        </div>
      )}

      {/* No Payroll */}
      {!loading && !payrollData && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <DollarSign className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No payroll generated for {monthOptions.find(m => m.value === selectedMonth)?.label}</p>
          <p className="text-sm text-gray-400">Click "Generate Payroll" to create payroll for this month</p>
        </div>
      )}

      {/* Payroll Table */}
      {!loading && payrollData && payrollData.records?.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              {monthOptions.find(m => m.value === selectedMonth)?.label} Payroll
            </h3>
            <span className="text-sm text-gray-500">
              {payrollData.records.length} staff members
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deductions</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Payable</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payrollData.records.map((record, index) => (
                  <tr key={record.staff_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900">{record.full_name}</div>
                        <div className="text-sm text-gray-500">{record.employee_id}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div>{record.department}</div>
                      <div className="text-xs text-gray-400">{record.designation}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="text-sm">
                        <span className="font-medium">{record.present_days}</span>
                        <span className="text-gray-400">/{record.working_days} days</span>
                      </div>
                      <div className={`text-xs ${record.attendance_percentage >= 90 ? 'text-green-600' : record.attendance_percentage >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {record.attendance_percentage}%
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {formatCurrency(record.actual_gross)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-red-600">
                      {formatCurrency(record.actual_deductions)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-lg font-bold text-blue-600">
                        {formatCurrency(record.net_payable)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(record.status)}
                      {record.paid_on && (
                        <div className="text-xs text-gray-400 mt-1">
                          {formatDate(record.paid_on)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {record.status === 'Pending' && record.net_payable > 0 && (
                        <button
                          onClick={() => openPaymentModal(record)}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          Pay Now
                        </button>
                      )}
                      {record.status === 'Paid' && (
                        <button 
                          onClick={() => window.open(`/hr/print-salary-slip?staff_id=${record.staff_id}&month=${selectedMonth}`, '_blank')}
                          className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded hover:bg-blue-200"
                        >
                          View Slip
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100">
                <tr>
                  <td colSpan="3" className="px-4 py-3 text-right font-semibold">Total:</td>
                  <td className="px-4 py-3 text-right font-bold text-green-600">
                    {formatCurrency(payrollData.records.reduce((sum, r) => sum + (r.actual_gross || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">
                    {formatCurrency(payrollData.records.reduce((sum, r) => sum + (r.actual_deductions || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-blue-600 text-lg">
                    {formatCurrency(totals.totalPayroll)}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModal.show && paymentModal.staff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Process Payment</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Employee:</span>
                  <span className="font-medium">{paymentModal.staff.full_name}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Net Payable:</span>
                  <span className="font-bold text-lg text-blue-600">
                    {formatCurrency(paymentModal.staff.net_payable)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <select 
                  className="w-full px-3 py-2 border rounded-lg"
                  value={paymentDetails.method}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, method: e.target.value, bank_id: e.target.value === 'Cash' ? '' : (banks[0]?.id || '') })}
                >
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>

              {/* Bank Account Selection - Only for non-cash payments */}
              {paymentDetails.method !== 'Cash' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Account <span className="text-red-500">*</span>
                  </label>
                  <select 
                    className="w-full px-3 py-2 border rounded-lg"
                    value={paymentDetails.bank_id}
                    onChange={(e) => setPaymentDetails({ ...paymentDetails, bank_id: e.target.value })}
                    required
                  >
                    <option value="">-- Select Bank --</option>
                    {banks.map(bank => (
                      <option key={bank.id} value={bank.id}>
                        {bank.bank_name} - {bank.account_number} (Balance: ₹{(bank.current_balance || 0).toLocaleString('en-IN')})
                      </option>
                    ))}
                  </select>
                  {!paymentDetails.bank_id && (
                    <p className="text-xs text-red-500 mt-1">Bank selection is required</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reference/Transaction ID</label>
                <input 
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter reference number"
                  value={paymentDetails.reference}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, reference: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                <textarea 
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Optional remarks"
                  rows="2"
                  value={paymentDetails.remarks}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, remarks: e.target.value })}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setPaymentModal({ show: false, staff: null })}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={processPayment}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payroll;
